/**
 * Trip collection API calls: collaborative collections on the trip board,
 * their items, and the UnifiedSaveModal helper endpoints.
 */
import { apiRequest } from './request';

/**
 * Create a collection on the trip board
 * @param {string} tripId - Trip ID
 * @param {object} data - { name, description? }
 * @returns {object} Result with collection
 */
export function createTripCollection(tripId, data) {
  return apiRequest(`/api/trips/${tripId}/collections`, {
    method: 'POST',
    body: data,
    errorLabel: 'creating trip collection'
  });
}

/**
 * Get a specific collection with its items
 * @param {string} tripId - Trip ID
 * @param {string} collectionId - Collection ID
 * @returns {object} Result with collection and items
 */
export function getTripCollection(tripId, collectionId) {
  return apiRequest(`/api/trips/${tripId}/collections/${collectionId}`, {
    errorLabel: 'getting trip collection'
  });
}

/**
 * Update a collection
 * @param {string} tripId - Trip ID
 * @param {string} collectionId - Collection ID
 * @param {object} updates - { name?, description?, cover_image? }
 * @returns {object} Result with updated collection
 */
export function updateTripCollection(tripId, collectionId, updates) {
  return apiRequest(`/api/trips/${tripId}/collections/${collectionId}`, {
    method: 'PUT',
    body: updates,
    errorLabel: 'updating trip collection'
  });
}

/**
 * Delete a collection
 * @param {string} tripId - Trip ID
 * @param {string} collectionId - Collection ID
 * @returns {object} Result
 */
export function deleteTripCollection(tripId, collectionId) {
  return apiRequest(`/api/trips/${tripId}/collections/${collectionId}`, {
    method: 'DELETE',
    errorLabel: 'deleting trip collection'
  });
}

/**
 * Copy a collection to personal collections
 * @param {string} tripId - Trip ID
 * @param {string} collectionId - Collection ID
 * @returns {object} Result with new personal collection
 */
export function copyTripCollectionToPersonal(tripId, collectionId) {
  return apiRequest(`/api/trips/${tripId}/collections/${collectionId}/copy`, {
    method: 'POST',
    errorLabel: 'copying trip collection'
  });
}

/**
 * Add item to a collection
 * @param {string} tripId - Trip ID
 * @param {string} collectionId - Collection ID
 * @param {object} data - { contentType, contentId, notes?, contentDetails? }
 * @returns {object} Result with item
 */
export function addItemToTripCollection(tripId, collectionId, data) {
  return apiRequest(`/api/trips/${tripId}/collections/${collectionId}/items`, {
    method: 'POST',
    body: data,
    errorLabel: 'adding item to trip collection'
  });
}

/**
 * Bulk add items to a collection
 * @param {string} tripId - Trip ID
 * @param {string} collectionId - Collection ID
 * @param {array} items - Array of { contentType, contentId, contentDetails?, notes? }
 * @returns {object} Result with added/skipped counts
 */
export function bulkAddItemsToTripCollection(tripId, collectionId, items) {
  return apiRequest(`/api/trips/${tripId}/collections/${collectionId}/items/bulk`, {
    method: 'POST',
    body: { items },
    errorLabel: 'bulk adding items to trip collection'
  });
}

/**
 * Remove item from a collection
 * @param {string} tripId - Trip ID
 * @param {string} collectionId - Collection ID
 * @param {number} itemId - Item ID
 * @returns {object} Result
 */
export function removeItemFromTripCollection(tripId, collectionId, itemId) {
  return apiRequest(`/api/trips/${tripId}/collections/${collectionId}/items/${itemId}`, {
    method: 'DELETE',
    errorLabel: 'removing item from trip collection'
  });
}

/**
 * Remove item from a trip collection by content type and content ID
 * @param {string} tripId - Trip ID
 * @param {string} collectionId - Collection ID
 * @param {string} contentType - Content type (e.g., 'activity')
 * @param {string} contentId - Content ID
 * @returns {object} Result
 */
export function removeItemFromTripCollectionByContent(tripId, collectionId, contentType, contentId) {
  return apiRequest(
    `/api/trips/${tripId}/collections/${collectionId}/items/by-content?contentType=${encodeURIComponent(contentType)}&contentId=${encodeURIComponent(contentId)}`,
    {
      method: 'DELETE',
      errorLabel: 'removing item from trip collection by content'
    }
  );
}

/**
 * Move one or more items from one collection to another within the same trip
 * @param {string} tripId
 * @param {string} fromCollectionId
 * @param {string} toCollectionId
 * @param {number[]} itemIds
 * @returns {object} Result { success, movedCount, skippedCount }
 */
export function moveItemsBetweenTripCollections(tripId, fromCollectionId, toCollectionId, itemIds) {
  return apiRequest(`/api/trips/${tripId}/collections/${fromCollectionId}/items/move`, {
    method: 'POST',
    body: { toCollectionId, itemIds },
    errorLabel: 'moving items between collections'
  });
}

/**
 * Reorder items in a collection
 * @param {string} tripId - Trip ID
 * @param {string} collectionId - Collection ID
 * @param {number[]} itemIds - Ordered array of item IDs
 * @returns {object} Result
 */
export function reorderTripCollectionItems(tripId, collectionId, itemIds) {
  return apiRequest(`/api/trips/${tripId}/collections/${collectionId}/items/reorder`, {
    method: 'PUT',
    body: { itemIds },
    errorLabel: 'reordering collection items'
  });
}

/**
 * Get user's editable trips with their boards and collections
 * Used by UnifiedSaveModal to show trip collections as save destinations
 * @returns {object} Result with trips array
 */
export function getEditableTripsWithCollections() {
  return apiRequest('/api/trips/user/me/editable-with-collections', {
    errorLabel: 'getting editable trips with collections'
  });
}

/**
 * Check which trip collections contain a specific item
 * Used by UnifiedSaveModal to pre-select collections that already have the item
 * @param {string} contentType - Type of content ('place', 'activity', etc)
 * @param {string} contentId - Content ID
 * @returns {object} Result with collections array
 */
export function getTripCollectionsContainingItem(contentType, contentId) {
  return apiRequest(
    `/api/trips/collections/containing-item?contentType=${encodeURIComponent(contentType)}&contentId=${encodeURIComponent(contentId)}`,
    {
      errorLabel: 'checking trip collections containing item'
    }
  );
}
